import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, PackagePlus, UserMinus } from 'lucide-react';
import {
  isCourierAssignableOrder,
  MAX_COURIER_BATCH_ORDERS,
  useTranslation,
  useAuthSession,
} from '@dukanti/shared';
import type { AdminOrderReviewFilter, OrderStatus, StoreOrderSortBy } from '@dukanti/shared';
import { supabase } from '@/lib/supabase';
import { useCurrentStore } from '@/hooks/use-current-store';
import { useCourierPermissions } from '@/hooks/use-courier-permissions';
import { useStoreOrders } from '@/hooks/use-store-orders';
import { useUnassignOrdersFromCourier } from '@/hooks/use-unassign-orders-from-courier';
import { Button } from '@/components/ui/button';
import { useStore } from '@/features/store-settings/use-store';
import {
  OrdersFilterChips,
  OrdersSortDropdown,
  OrdersSearchInput,
  OrdersTable,
  OrdersMobileCards,
  OrdersListSkeleton,
  OrdersEmpty,
  CourierAssignmentDialog,
} from '@/features/orders';

const ADMIN_ORDER_FILTER_STATUSES: readonly OrderStatus[] = [
  'ordered',
  'shipped',
  'delivered',
  'cancelled',
  'rejected',
];

function parseStatusFilter(rawStatus: string | null): OrderStatus | null {
  if (!rawStatus) return 'ordered';
  if (rawStatus === 'all') return null;
  return ADMIN_ORDER_FILTER_STATUSES.includes(rawStatus as OrderStatus)
    ? (rawStatus as OrderStatus)
    : 'ordered';
}

function parseOrderSort(rawSort: string | null): StoreOrderSortBy {
  if (rawSort === 'subtotal_asc') return 'total_asc';
  if (rawSort === 'subtotal_desc') return 'total_desc';
  return rawSort === 'total_asc' || rawSort === 'total_desc' || rawSort === 'created_at_asc'
    ? rawSort
    : 'created_at_desc';
}

function parseReviewFilter(rawReview: string | null): AdminOrderReviewFilter | null {
  return rawReview === 'awaiting_approval' ? 'awaiting_approval' : null;
}

/**
 * F-021: Orders list route orchestrator.
 *
 * Wires together the Stitch-generated components with the real data hook.
 * Manages URL-synced state for status filter, sort, and search query.
 * Responsive layout: table on desktop (md:+), card stack on mobile.
 */
export function OrdersListRoute(): React.JSX.Element {
  const { t, dir } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isFirstRender = useRef(true);

  const authSession = useAuthSession(supabase);
  const { storeId } = useCurrentStore(authSession);
  const storeQuery = useStore(storeId);
  const courierPermissions = useCourierPermissions(storeId);
  const canManageCouriers = courierPermissions.data?.canManageCouriers === true;
  const unassignMutation = useUnassignOrdersFromCourier(storeId ?? '');

  // Initialise filter/sort/search from URL query params.
  // Default: Ordered status, newest first, no search.
  const [selectedStatus, setSelectedStatus] = useState<OrderStatus | null>(() =>
    parseStatusFilter(searchParams.get('status')),
  );
  const [selectedReviewFilter, setSelectedReviewFilter] = useState<AdminOrderReviewFilter | null>(
    () => parseReviewFilter(searchParams.get('review')),
  );
  const [sortBy, setSortBy] = useState<StoreOrderSortBy>(() =>
    parseOrderSort(searchParams.get('sort')),
  );
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') ?? '');
  const [selectedOrderIds, setSelectedOrderIds] = useState<ReadonlySet<string>>(() => new Set());
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);

  useEffect(() => {
    const rawSort = searchParams.get('sort');
    if (rawSort === 'subtotal_asc' || rawSort === 'subtotal_desc') {
      const migrated = new URLSearchParams(searchParams);
      migrated.set('sort', parseOrderSort(rawSort));
      setSearchParams(migrated, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Sync state changes back to the URL (replace, so we don't clutter history).
  // Skip the first render so we don't overwrite the URL on initial mount.
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const params = new URLSearchParams();
    if (selectedReviewFilter) {
      params.set('review', selectedReviewFilter);
    } else if (selectedStatus && selectedStatus !== 'ordered') {
      params.set('status', selectedStatus);
    }
    if (sortBy !== 'created_at_desc') {
      params.set('sort', sortBy);
    }
    if (searchQuery) {
      params.set('search', searchQuery);
    }
    setSearchParams(params, { replace: true });
  }, [selectedReviewFilter, selectedStatus, sortBy, searchQuery, setSearchParams]);

  useEffect(() => {
    setSelectedOrderIds(new Set());
  }, [selectedReviewFilter, selectedStatus, sortBy, searchQuery]);

  const { rows, isLoading, isError, hasNextPage, isFetchingNextPage, fetchNextPage, refetch } =
    useStoreOrders({
      storeId,
      status: selectedReviewFilter ? 'ordered' : selectedStatus,
      reviewFilter: selectedReviewFilter,
      sortBy,
      search: searchQuery || null,
    });

  const handleRowClick = useCallback(
    (orderId: string) => {
      navigate(`/orders/${orderId}`);
    },
    [navigate],
  );

  const handleSelectionChange = useCallback((orderId: string, selected: boolean) => {
    setSelectedOrderIds((current) => {
      const next = new Set(current);
      if (selected) {
        if (next.size >= MAX_COURIER_BATCH_ORDERS) return current;
        next.add(orderId);
      } else {
        next.delete(orderId);
      }
      return next;
    });
  }, []);

  const selectedOrders = rows.filter((row) => selectedOrderIds.has(row.id));
  const hasTooManySelectedOrders = selectedOrderIds.size > MAX_COURIER_BATCH_ORDERS;
  const hasManualApprovalPendingSelectedOrders = selectedOrders.some(
    (order) => order.manual_approval_status === 'pending',
  );
  const hasInvalidSelectedOrders =
    selectedOrders.length !== selectedOrderIds.size ||
    selectedOrders.some((order) => !isCourierAssignableOrder(order));
  const selectedAssignedOrders = selectedOrders.filter((order) => order.courier_assignment);
  const hasMixedCourierAssignmentSelection =
    selectedAssignedOrders.length > 0 && selectedAssignedOrders.length !== selectedOrders.length;
  const canUnassignSelectedOrders =
    selectedOrders.length > 0 &&
    selectedAssignedOrders.length === selectedOrders.length &&
    !hasTooManySelectedOrders &&
    !hasInvalidSelectedOrders;

  const showSkeleton = isLoading;
  const isEmpty = !isLoading && rows.length === 0;
  const isPopulated = !isLoading && rows.length > 0;

  return (
    <main
      dir={dir}
      data-testid="orders-list-route"
      className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8"
    >
      {/* Page header */}
      <div className="mb-6 flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
        <h1 className="font-display text-foreground text-start text-[28px] font-bold leading-tight tracking-tight">
          {t('admin.orders.title')}
        </h1>
        <Button type="button" onClick={() => navigate('/orders/prepare')}>
          <PackagePlus className="me-2 h-4 w-4" />
          {t('admin.orders.prepare.openCta' as never)}
        </Button>
      </div>

      {/* Toolbar: search, sort, filter chips */}
      <div className="border-border bg-card shadow-admin-card mb-4 flex flex-col gap-4 rounded-lg border p-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <OrdersSearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder={t('admin.orders.searchPlaceholder')}
          />
          <OrdersSortDropdown value={sortBy} onChange={setSortBy} />
        </div>
        <OrdersFilterChips
          selected={selectedStatus}
          selectedReviewFilter={selectedReviewFilter}
          onChange={(nextStatus) => {
            setSelectedReviewFilter(null);
            setSelectedStatus(nextStatus);
          }}
          onReviewFilterChange={(nextReviewFilter) => {
            setSelectedReviewFilter(nextReviewFilter);
            setSelectedStatus('ordered');
          }}
        />
      </div>

      {/* Error state */}
      {isError ? (
        <div
          role="alert"
          data-testid="orders-list-error"
          className="border-border bg-card shadow-admin-card mx-auto max-w-lg rounded-lg border p-8 text-center"
        >
          <div className="text-destructive mb-4 flex justify-center">
            <AlertCircle className="h-10 w-10" strokeWidth={1.5} />
          </div>
          <h2 className="font-display text-foreground mb-2 text-xl">{t('common.error')}</h2>
          <p className="text-muted-foreground mb-5 text-sm">{t('errors.networkFailed')}</p>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              void refetch();
            }}
            data-testid="orders-list-error-retry"
          >
            {t('common.retry')}
          </Button>
        </div>
      ) : null}

      {/* Loading skeleton */}
      {!isError && showSkeleton ? <OrdersListSkeleton /> : null}

      {/* Empty state */}
      {!isError && isEmpty ? (
        <OrdersEmpty filter={selectedStatus} reviewFilter={selectedReviewFilter} />
      ) : null}

      {/* Populated list: table (desktop) + cards (mobile) */}
      {!isError && isPopulated ? (
        <div className="space-y-6">
          {canManageCouriers && selectedOrderIds.size > 0 ? (
            <div className="border-border bg-card shadow-admin-card flex flex-col gap-3 rounded-lg border p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-start">
                <p className="text-foreground text-sm font-medium">
                  {t('admin.orders.courierAssignment.selectedCount' as never, {
                    count: selectedOrderIds.size,
                  })}
                </p>
                {hasTooManySelectedOrders || hasInvalidSelectedOrders ? (
                  <p className="text-destructive mt-1 text-sm">
                    {t(
                      (hasTooManySelectedOrders
                        ? 'admin.orders.courierAssignment.errors.tooManyOrders'
                        : hasManualApprovalPendingSelectedOrders
                          ? 'admin.orders.courierAssignment.errors.manualApprovalRequired'
                          : 'admin.orders.courierAssignment.errors.invalidStatus') as never,
                    )}
                  </p>
                ) : null}
                {hasMixedCourierAssignmentSelection ? (
                  <p className="text-muted-foreground mt-1 text-sm">
                    {t('admin.orders.courierAssignment.unassignRequiresAssignedSelection' as never)}
                  </p>
                ) : null}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setSelectedOrderIds(new Set())}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  type="button"
                  disabled={hasTooManySelectedOrders || hasInvalidSelectedOrders}
                  onClick={() => setAssignmentDialogOpen(true)}
                >
                  {t('admin.orders.courierAssignment.bulkAssign' as never)}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={!canUnassignSelectedOrders || unassignMutation.isPending}
                  onClick={() =>
                    unassignMutation.mutate(
                      { orderIds: selectedOrders.map((order) => order.id) },
                      {
                        onSuccess: (result) => {
                          if (result.success) setSelectedOrderIds(new Set());
                        },
                      },
                    )
                  }
                >
                  <UserMinus className="size-4" aria-hidden />
                  <span>{t('admin.orders.courierAssignment.bulkUnassign' as never)}</span>
                </Button>
              </div>
            </div>
          ) : null}

          <OrdersTable
            rows={rows}
            onRowClick={handleRowClick}
            canSelectOrders={canManageCouriers}
            selectedOrderIds={selectedOrderIds}
            onSelectionChange={handleSelectionChange}
          />
          <OrdersMobileCards
            rows={rows}
            onCardClick={handleRowClick}
            canSelectOrders={canManageCouriers}
            selectedOrderIds={selectedOrderIds}
            onSelectionChange={handleSelectionChange}
          />

          {/* Load more */}
          {hasNextPage ? (
            <div className="flex justify-center">
              <Button
                type="button"
                variant="outline"
                onClick={() => void fetchNextPage()}
                disabled={isFetchingNextPage}
                data-testid="orders-load-more"
              >
                {isFetchingNextPage ? t('admin.orders.loadingMore') : t('admin.orders.loadMore')}
              </Button>
            </div>
          ) : null}

          {storeId && canManageCouriers ? (
            <CourierAssignmentDialog
              open={assignmentDialogOpen}
              storeId={storeId}
              storeName={storeQuery.data?.name ?? ''}
              orders={selectedOrders}
              onOpenChange={(open) => {
                setAssignmentDialogOpen(open);
                if (!open) setSelectedOrderIds(new Set());
              }}
            />
          ) : null}
        </div>
      ) : null}
    </main>
  );
}
